import { ProductAccountDTO } from 'api-models'
import { GButton } from 'components/basic-blocks/'
import { useMemo } from 'react'
import {
	RiArrowDownSLine,
	RiArrowRightSLine,
	RiCheckLine
} from 'react-icons/ri'
import { classNames, sanitizeURL, setItemsToLocalStorage } from 'utils'
import { capitalizeFirstLetter } from '../../../utils/text'
import { getAccountBadgeColorByStatus } from '../../../utils/user'
import { useAccountStore, useAssetStore, useUiStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { GBadge } from '../../../components/basic-blocks/g-badge'
import { GSwitcher } from '../../../components/basic-blocks/g-switcher/g-switcher'
import { FiPlus } from 'react-icons/fi'
import { NewWorkspaceAvatar } from '../../../components/workspace-avatar/workspace-avatar-new'
import { useNavigateWithParams } from '../../../hooks/use-navigate-with-params'
import { WebsiteAvatar } from 'components'
import { clearFiltersFromStorage } from '../../../utils/filter-presistence'

export const AccountSwitcher = ({
	onCreateWorkspace,
	type = 'default'
}: {
	onCreateWorkspace?: () => void
	type?: 'default' | 'dropdown'
}) => {
	const navigate = useNavigateWithParams()

	const {
		accounts,
		setAccount,
		account,
		setWantsToSeeDemoData,
		getSubscriptionByAccountId,
		getAllAccounts
	} = useAccountStore(
		useShallow((s) => ({
			accounts: s.accounts,
			setAccount: s.setAccount,
			account: s.account,
			getAllAccounts: s.getAllAccounts,
			setWantsToSeeDemoData: s.setWantsToSeeDemoData,
			getSubscriptionByAccountId: s.getSubscriptionByAccountId
		}))
	)
	const { accountsWithAssets, setAsset, setAssetForId, setDemoMode } =
		useAssetStore(
			useShallow((s) => ({
				accountsWithAssets: s.accountsWithAssets,
				setAsset: s.setAsset,
				setAssetForId: s.setAssetForId,
				setDemoMode: s.setDemoMode
			}))
		)
	const { user } = useUserStore((s) => ({ user: s.user }))
	const userAccessInfo = useMemo(() => {
		return account?.user_access?.find(
			(userAccess) => userAccess.user_id === user?.id
		)
	}, [account])

	const text =
		userAccessInfo?.access_level === 'READ'
			? 'Analyst'
			: userAccessInfo?.access_level === 'WRITE'
			? 'Manager'
			: userAccessInfo?.access_level === 'OWNER'
			? 'Owner'
			: 'Other'
	const color =
		userAccessInfo?.access_level === 'READ'
			? 'green'
			: userAccessInfo?.access_level === 'WRITE'
			? 'purple'
			: userAccessInfo?.access_level === 'OWNER'
			? 'primary'
			: 'primary'
	return (
		<>
			<div className="asset-switcher hidden md:flex items-center w-full">
				<GSwitcher<ProductAccountDTO>
					items={accounts}
					selectedItem={<SelectedItem />}
					filter={(item, query) =>
						item.name.toLowerCase().includes(query.toLowerCase())
					}
					renderOption={(i, handleClose) => renderOption(i, handleClose)}
					footer={
						<SwitcherContentFooter onCreateWorkspace={onCreateWorkspace} />
					}
					placeholder="Filter workspaces..."
					disableSearch
					position={type === 'default' ? 'right-start' : undefined}
					offset={type === 'default' ? [0, -22] : undefined}
				/>
				<div className="block md:hidden">
					<GSwitcher<ProductAccountDTO>
						width="w-full"
						items={accounts}
						selectedItem={<SelectedItem />}
						filter={(item, query) =>
							item.name.toLowerCase().includes(query.toLowerCase())
						}
						renderOption={(i) => i.name.toString()}
						footer={
							<SwitcherContentFooter onCreateWorkspace={onCreateWorkspace} />
						}
						placeholder="Filter workspaces..."
						responsive={true}
						disableSearch
					/>
				</div>
			</div>
		</>
	)

	function renderOption(option: ProductAccountDTO, handleClose: () => void) {
		const path = window.location.pathname.split('/')
		const pageIdentifier = path[5]
	
		const settingsIdentifier = path.includes('settings')
		const workspaceIdentifier = path.includes('workspace')
		const assetIdentifier = path.includes('asset')
		const settingsSubIdentifier =
			path.includes('settings') && path[path.length - 1]
	
		const handleSelectingAccount = async (account: ProductAccountDTO) => {
			setAccount(account)
			account?.billing?.subscription && getSubscriptionByAccountId(account.id)
			const selectedAccount = accountsWithAssets.find(
				(item) => item.id === account?.id
			)
			const workspaceId = account.id
			const assetId = selectedAccount?.assets?.length
				? selectedAccount?.assets[0]?.id
				: 'website-demo'
	
			setItemsToLocalStorage([
				{ key: 'selected_asset', value: assetId },
				{ key: 'selected_account', value: workspaceId }
			])

			clearFiltersFromStorage()

			setWantsToSeeDemoData(false)
			settingsIdentifier
				? assetId === 'website-demo' && assetIdentifier
					? navigate(`/settings/workspace/${workspaceId}/preferences`)
					: navigate(
							`/settings${
								!workspaceIdentifier && !assetIdentifier ? '/user' : ''
							}${workspaceIdentifier ? `/workspace/${workspaceId}` : ''}${
								assetIdentifier ? `/asset/${assetId}` : ''
							}/${settingsSubIdentifier}`
					  )
				: navigate(
						`/workspace/${workspaceId}/asset/${assetId}/${
							pageIdentifier ? pageIdentifier : ''
						}`
				  )
			handleClose()
	
			if (selectedAccount && assetId === 'website-demo') {
				await setAssetForId(selectedAccount?.id, 'website-demo')
			} else if (selectedAccount?.assets?.length) {
				setDemoMode(false)
				setAsset(selectedAccount?.assets[0])
			}
		}
	
		const buildBadgeText = (account: ProductAccountDTO) => {
			if (account.status === 'INACTIVE') return 'Inactive'
			if (account.status === 'SETUP') {
				return account.setup?.step === 'ACCOUNT_CREATED' ||
					account.setup?.step === 'PLAN_SELECTED'
					? 'Pending activation'
					: 'Setup'
			}
			if (account?.billing?.subscription?.type === 'TRIAL') return 'Trial'
			return capitalizeFirstLetter(account.status)
		}
		const badgeText = buildBadgeText(option)
	
		return (
			<div
				key={option.id}
				onClick={() => handleSelectingAccount(option)}
				className={classNames(
					' font-medium hover:bg-t-google-btn group my-2 hover:rounded-3xl flex items-center py-3 px-4 text-lg space-x-3 cursor-pointer w-full',
					`${option.id === account?.id && 'bg-t-google-btn rounded-3xl'}`
				)}
			>
				<div className="flex items-center justify-between gap-4 space-y-0 w-full">
					<div className="flex items-center justify-start w-full">
						<div className="min-w-[2.5rem] pr-2">
							<NewWorkspaceAvatar workspace={option} size={7} />
						</div>
						<div>
							<div className="truncate max-w-[200px] text-t-google-ad text-base font-normal">
								{sanitizeURL(option?.name)}
							</div>
							{option?.billing?.organization?.description && (
								<div className="text-sm text-t-secondary truncate max-w-[200px]">
									{option?.billing?.organization?.description}
								</div>
							)}
						</div>
						{badgeText ? (
							<div className="pl-2">
								<GBadge
									className={classNames('rounded-md align-center')}
									text={badgeText}
									color={getAccountBadgeColorByStatus(
										option.status,
										option?.billing?.subscription?.type
									)}
								/>
							</div>
						) : null}
					</div>
					{option.id === account?.id && (
						<div>
							<RiCheckLine className="text-primary-500 w-4 h-4" />
						</div>
					)}
				</div>
			</div>
		)
	}

	function SelectedItem() {
		if (type === 'dropdown') {
			return (
				<div className="w-52 h-10 flex items-center px-4 py-2.5 text-md text-t-base-blue bg-transparent border border-t-dark-blue shadow-outline-workspace space-x-4 rounded-full cursor-pointer">
					<div className="flex flex-row items-center w-full">
						<div className="flex items-center gap-2">
							<div>
								<WebsiteAvatar website={account} size={6} />
							</div>
							<span className="flex flex-col truncate max-w-[8rem]">
								{sanitizeURL(account?.name)}
							</span>
						</div>
					</div>
					<div>
						<RiArrowDownSLine className="h-5 w-5 text-t-dark-blue" />
					</div>
				</div>
			)
		}
		return (
			<div className="w-full flex items-center p-2 text-t-google-tooltip hover:bg-t-google-btn rounded-3xl  max-w-full cursor-pointer">
				<div className="flex items-center w-full ">
					{account && (
						<div className="h-8 flex px-2 items-center justify-between rounded-md">
							<NewWorkspaceAvatar workspace={account} size={8} />
						</div>
					)}
					<div className="text-left items-center flex-col flex-1 hidden md:block">
						{!account?.name ? (
							<p
								className={
									'text-md font-medium font-inter text-t-heading px-2 truncate'
								}
							>
								Workspaces
							</p>
						) : (
							<p
								className={
									'text-md font-medium leading-normal tracking-tight font-inter text-t-google-tooltip w-32 truncate'
								}
							>
								{sanitizeURL(account?.name)}
							</p>
						)}

						{account && (
							<GBadge className="rounded-md" color={color} text={text} />
						)}
					</div>
				</div>
				<div>
					<RiArrowRightSLine className="h-5 w-5 text-t-base-blue" />
				</div>
			</div>
		)
	}
	function SwitcherContentFooter({
		onCreateWorkspace
	}: {
		onCreateWorkspace?: () => void
	}) {
		if (!onCreateWorkspace) return <></>

		return (
			<div className="flex flex-grow p-2">
				<GButton
					color="neutral"
					labelClassName="font-base font-normal leading-5"
					className="w-full bg-transparent rounded-full p-3  transition-all duration-300  disabled:!shadow-none border-t-google-btn text-t-base-blue hover:bg-t-base-blue hover:text-t-smoke"
					icon={FiPlus}
					onClick={() => navigate('/start/standard')}
				>
					Create new workspace
				</GButton>
			</div>
		)
	}
}
